import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Flex, Hide, HStack, Image, Stack, Text, useBreakpointValue, VStack } from '@chakra-ui/react'
import loadingSpinner from 'assets/lottie/loader_wicar.gif'
import { useDisplayModal, usePayment, useVehicles } from 'context'
import { PaymentSubscriptionInfoType } from 'context/types'
import dayjs from 'dayjs'
import { themeColors } from 'theme'

import { CardContainer } from 'components/CustomCard'
import CustomDivider from 'components/CustomDivider'
import { CustomModal } from 'components/CustomModal'
import PlanItems from 'components/PlanItems'
import { CheckmarkIcon, DecreaseIcon, ExclamationIcon, IncreaseIcon } from 'components/SVGS'
import { customToast } from 'components/ToastCustom'
import VehicleInformation from 'components/VehicleInformation'

const { text: { smallText }, icon: { errorIconColor } } = themeColors

const SubscriptionInfo: React.FC = () => {
  const breakpoint = useBreakpointValue({ base: 'small', md: 'desktop' })
  const { cancelSubscription, getCurrentSubscriptionByVehicleId, getInstallationPaymentStatus, paymentState:
    { loading, state: { currentSubscription, isPaidInstallation } }
  } = usePayment()
  const { vehicleState: { state: { vehicle } } } = useVehicles()
  const { openDialog, closeModal } = useDisplayModal()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [loadingCancel, setLoadingCancel] = useState(false)
  const [subscription, setSubscription] = useState<PaymentSubscriptionInfoType | null>(null)

  useEffect(() => {
    if (vehicle) {
      getCurrentSubscriptionByVehicleId(vehicle.id)
      getInstallationPaymentStatus(vehicle.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle])

  useEffect(() => {
    currentSubscription && setSubscription(currentSubscription)
  }, [currentSubscription, vehicle])

  const handleNavigation = () => {
    vehicle?.isPremium
      ? navigate('/subscription-info', {
        state: {
          subscription: {
            vehicle,
            subscriptionType: 'SUSCR-RENOV',
            paymentDate: subscription?.contractRenewDate
          }
        }
      })
      : !isPaidInstallation && subscription?.isDevice
        ? navigate('/hire-wicar', {
          state: {
            subscription: {
              vehicle,
              subscriptionType: 'SUSCR-RENOV',
              paymentDate: subscription?.contractRenewDate
            }
          }
        })
        : navigate('/hire-wicar', {
          state: {
            subscription: {
              vehicle,
              subscriptionType: isPaidInstallation ? 'INSTALL' : 'SUSCR-PRIMER',
            }
          }
        })
  }

  const handleCancelSubscription = async () => {
    if (vehicle) {
      closeModal()
      setLoadingCancel(true)

      const result = await cancelSubscription(vehicle.id)

      setLoadingCancel(false)

      if (result === '') {
        customToast.showToast({
          status: 'success',
          description: `La suscripción de tu vehículo ${vehicle.plateNumber} fue cancelada correctamente`,
        })
        getCurrentSubscriptionByVehicleId(vehicle.id)
      } else customToast.showToast({ description: result })
    }
  }

  const handleDeactivateRenewal = () => {
    openDialog({
      content: <CustomModal
        title='¿Quieres desactivar tu renovación automática para este vehículo?'
        message={'Si realizas esta acción, vas a tener que pagar manualmente tu suscripción y ya no te haremos el'
          + ' cobro de forma automática'}
        primaryButton
        primaryTextButton='Desactivar'
        secondaryButton
        secondaryTextButton='Volver'
        closeAcceptPress={handleCancelSubscription}
        closeGoBackPress={closeModal}
      />,
      variant: 'primary'
    })
  }

  const handleSecondSubmit = () => {
    navigate('/contract-subscription', {
      state: {
        subscription: {
          vehicle,
          subscriptionType: 'SOLO-SUSCR',
          paymentDate: subscription?.contractRenewDate,
        }
      }
    })
  }

  return (
    <CardContainer paddingY={4}>
      <Flex
        w={'full'}
        justifyContent='center'
        spacing={4}
        as={breakpoint === 'small' ? Stack : HStack}
        alignItems={{ md: 'flex-start' }}>
        {loading
          ?
          <VStack alignItems='center' w='100%'>
            <Image
              w={'120px'}
              h={'120px'}
              alt={'loading_wicar'}
              src={loadingSpinner}
            />
          </VStack>
          : <>
            <VStack alignItems='flex-start' spacing={4} w={{ md: '100%' }} >
              <Text textAlign='center' variant={'LB_SB'} >Mi suscripción</Text>
              <VehicleInformation {...vehicle} isOnline = {null} />
              <VStack spacing={2} borderLeftWidth={3} borderColor='white' pl={4} alignItems='flex-start'>
                <Text variant='SM_SB'>Tipo de suscripción</Text>
                <Text variant='SM'>{vehicle?.isPremium ? 'Premium' : 'Sin suscripción'}</Text>
              </VStack>
              <CustomDivider />
              {
                !!(subscription?.contractRenewDate || subscription?.subscriptionInfo?.date) &&
                <HStack alignItems='flex-start'>
                  {dayjs(subscription.subscriptionInfo?.date || subscription.contractRenewDate) > dayjs()
                    ? <CheckmarkIcon size={7} />
                    : <ExclamationIcon size={7} fill={errorIconColor} />
                  }
                  <VStack spacing={2} alignItems='flex-start'>
                    <Text variant='SM_SB'>
                      {dayjs(subscription.subscriptionInfo?.date || subscription.contractRenewDate) > dayjs()
                        ? 'Fecha próxima renovación:'
                        : 'Fecha de caducación:'
                      }
                    </Text>
                    <Text>
                      {
                        dayjs.utc(subscription.subscriptionInfo?.date || subscription.contractRenewDate)
                          .format('DD MMMM YYYY')
                      }
                    </Text>
                  </VStack>
                </HStack>
              }
              <Button
                w='full'
                whiteSpace='initial'
                disabled={loadingCancel}
                isLoading={loadingCancel}
                loadingText={'Desactivando...'}
                onClick={() => subscription?.subscriptionInfo
                  ? handleDeactivateRenewal()
                  : handleNavigation()}>
                {subscription?.subscriptionInfo
                  ? 'DESACTIVAR RENOVACIÓN AUTOMÁTICA'
                  : !isPaidInstallation && (vehicle?.isPremium || subscription?.isDevice)
                    ? 'RENOVAR'
                    : 'CONTRATAR PLAN PREMIUM'}
              </Button>
           {/*    {vehicle?.isPremium && subscription && !subscription.subscriptionInfo
                && dayjs(subscription.contractRenewDate) > dayjs() &&
                <Button variant='secondary' w='full' whiteSpace='initial' onClick={handleSecondSubmit}>
                  Activar renovación automática
                </Button>
              } */}
            </VStack>
            <Hide above='md'>
              <CustomDivider />
            </Hide>
            {/* <VStack alignItems={'flex-start'} w={{ md: '40%' }}>
              <Hide above='md'>
                <Button variant='link' onClick={() => setIsOpen(!isOpen)}
                  leftIcon={isOpen ? <DecreaseIcon size={7} /> : <IncreaseIcon size={7} />}>
                  Lo que incluye tu plan
                </Button>
              </Hide>
              {
                (isOpen || breakpoint === 'desktop') &&
                <VStack alignItems={'flex-start'} spacing={2}>
                  <Text color={smallText}>Lo que incluye</Text>
                  <PlanItems isPremium={vehicle?.isPremium} />
                </VStack>
              }
            </VStack> */}
          </>
        }
      </Flex>
    </CardContainer >
  )
}

export default SubscriptionInfo
